/**
 * Les briques communes de Consulter : la photo (ou les initiales), une ligne
 * « à savoir », et une ligne d'enfant avec ses signes — celles des écrans de
 * présences, aux couleurs de la gestion.
 */

import { CameraOff, ChevronRight, HeartPulse, LogOut, Shield } from 'lucide-react'
import { initiales } from '../../../../lib/presences/essentiels'
import type { LigneASavoir, Signes } from './regles'

export function Avatar({ photo, prenom, nom, taille }: { photo: string | null; prenom: string; nom: string; taille: 'sm' | 'lg' }) {
  const dim = taille === 'lg' ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm'
  if (photo) {
    return <img src={photo} alt="" loading="lazy" className={`${dim} flex-shrink-0 rounded-full object-cover ring-1 ring-gray-200`} />
  }
  return (
    <span
      aria-hidden
      className={`${dim} flex flex-shrink-0 items-center justify-center rounded-full bg-amber-50 font-semibold text-amber-800 ring-1 ring-amber-100`}
    >
      {initiales(prenom, nom)}
    </span>
  )
}

export function LigneASavoirVue({ ligne }: { ligne: LigneASavoir }) {
  const icone =
    ligne.cle === 'photo' ? <CameraOff className="h-4 w-4" />
      : ligne.cle === 'depart' ? <LogOut className="h-4 w-4" />
        : ligne.cle === 'besoins' || ligne.cle === 'activites' ? <Shield className="h-4 w-4" />
          : <HeartPulse className="h-4 w-4" />
  return (
    <div className={`flex items-start gap-2 text-sm ${ligne.alerte ? 'text-amber-900' : 'text-gray-800'}`}>
      <span className={`mt-0.5 flex-shrink-0 ${ligne.alerte ? 'text-amber-600' : 'text-gray-400'}`}>{icone}</span>
      <dt className="w-24 flex-shrink-0 text-gray-500">{ligne.libelle}</dt>
      <dd className={`min-w-0 flex-1 ${ligne.alerte ? 'font-medium' : ''}`}>{ligne.texte}</dd>
    </div>
  )
}

/** Les signes d'une ligne d'enfant, comme dans la liste des présences. */
export function SignesVue({ signes }: { signes: Signes | null }) {
  if (!signes) return null
  return (
    <>
      {signes.sante && <HeartPulse className="h-4 w-4 flex-shrink-0 text-red-600" aria-label="Santé : à savoir" />}
      {signes.besoins && <Shield className="h-4 w-4 flex-shrink-0 text-purple-600" aria-label="Besoins particuliers" />}
      {signes.seul && <LogOut className="h-4 w-4 flex-shrink-0 text-blue-600" aria-label="Rentre seul" />}
      {signes.pasDePhoto && <CameraOff className="h-4 w-4 flex-shrink-0 text-amber-700" aria-label="Pas de photo" />}
    </>
  )
}

/** Une ligne d'enfant : sa photo, son nom, ses signes, une ligne d'infos ; le clic ouvre la fiche. */
export function LigneEnfant({
  photo, prenom, nom, signes, sousTitre, onOuvrir,
}: {
  photo: string | null
  prenom: string
  nom: string
  signes: Signes | null
  sousTitre: string | null
  onOuvrir: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOuvrir}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-amber-50/60 focus:bg-amber-50/60 focus:outline-none"
        aria-label={`Fiche de ${prenom} ${nom}`}
      >
        <Avatar photo={photo} prenom={prenom} nom={nom} taille="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-base font-semibold text-gray-900">{prenom}</span>
            <span className="truncate text-sm text-gray-600">{nom}</span>
            <SignesVue signes={signes} />
          </div>
          {sousTitre && <div className="truncate text-xs text-gray-500">{sousTitre}</div>}
        </div>
        <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-300" />
      </button>
    </li>
  )
}
